
 import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/api';
import Spinner from '../../components/ui/Spinner';
import MessageBox from '../../components/ui/MessageBox';
import Heading from "../../components/ui/Heading"; 

// StatCard Component
const StatCard = ({ title, value, icon, color }) => {
  const accent = color.includes("blue") ? "bg-sky-500" : "bg-emerald-500";

  return (
  <div className="group overflow-hidden rounded-lg border border-slate-200 bg-white p-4 shadow-sm transition hover:border-teal-200 hover:shadow-md">
    <div className={`mb-4 h-1.5 w-16 rounded-full ${accent}`} />
    <div className="flex items-center">
      <div className="mr-4 flex h-11 w-11 items-center justify-center rounded-lg bg-teal-50 text-teal-700 transition group-hover:bg-teal-100">{icon}</div>
      <div>
        <p className="text-sm font-medium text-slate-500">{title}</p>
        <p className="mt-1 text-xl font-semibold tracking-normal text-slate-900">{value}</p>
      </div>
    </div>
  </div>
  );
};

// StatTable Component
const StatTable = ({ title, data, type }) => {
  const formatCurrency = (value) =>
    (value || 0).toLocaleString('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

  return (
    <div className="mt-4 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm sm:mt-6">
      <h3
        className={`p-3 text-base font-semibold text-white ${
          type === 'po' ? 'bg-sky-600' : 'bg-teal-600'
        }`}
      >
        {title}
      </h3>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-500">
                Period
              </th>
              <th className="px-4 py-3 text-right text-xs font-bold uppercase text-slate-500">
                {type === 'po' ? 'PO Qty' : 'Inv Qty'}
              </th>
              <th className="px-4 py-3 text-right text-xs font-bold uppercase text-slate-500">
                {type === 'po' ? 'Order Total' : 'Inv Value'}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {data.map((item, index) => (
              <tr key={index} className="hover:bg-emerald-50/60">
                <td className="whitespace-nowrap px-4 py-4 text-sm font-semibold text-slate-900">
                  {item.Orders || item.Sales}
                </td>
                <td className="whitespace-nowrap px-4 py-4 text-right text-sm text-slate-500">
                  {item.PoQty || item.InvQty || 'N/A'}
                </td>
                <td className="whitespace-nowrap px-4 py-4 text-right text-sm font-semibold text-slate-700">
                  {formatCurrency(item.OrderTotal || item.InvValue) || 'N/A'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({ po: [], sales: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api
      .getSalesPoStatement(user.UsrName)
      .then((data) =>
        setStats({ po: data.Table || [], sales: data.Table1 || [] })
      )
      .catch(() => setError('Failed to fetch dashboard data.'))
      .finally(() => setLoading(false));
  }, [user.UsrName]);

  const formatCurrency = (value) =>
    (value || 0).toLocaleString('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

  const todaySales = stats.sales.find((s) => s.Sales === 'Today');
  const todayPOs = stats.po.find((p) => p.Orders === 'Today');
  const monthSales = stats.sales.find((s) => s.Sales === 'ThisMonth');
  const monthPOs = stats.po.find((p) => p.Orders === 'ThisMonth');

  if (loading) return <Spinner />;
  if (error) return <MessageBox message={error} />;

  return (
    <div className="min-h-screen w-full">
      <div className="mx-auto w-full max-w-7xl p-0 sm:p-4">
      <Heading>Business Overview</Heading>

        {/* Stat Cards */}
        <div className="grid grid-cols-1 gap-4 mb-6 sm:grid-cols-2 lg:grid-cols-4 sm:gap-6 sm:mb-10">
          <StatCard
            title="Today's Sales"
            value={formatCurrency(todaySales?.InvValue)}
            color="border-green-500"
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            }
          />
          <StatCard
            title="Today's POs"
            value={formatCurrency(todayPOs?.OrderTotal)}
            color="border-blue-500"
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
            }
          />
          <StatCard
            title="This Month's Sales"
            value={formatCurrency(monthSales?.InvValue)}
            color="border-green-500"
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
              </svg>
            }
          />
          <StatCard
            title="This Month's POs"
            value={formatCurrency(monthPOs?.OrderTotal)}
            color="border-blue-500"
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            }
          />
        </div>

        {/* Tables */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 sm:gap-8">
          <StatTable title="Sales Breakdown" data={stats.sales} type="sales" />
          <StatTable title="PO Breakdown" data={stats.po} type="po" />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;


